import i18next from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { type DropzoneOptions, useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import {
  type AttachmentMode,
  filesAccept,
  fullAccept,
  mediaAccept
} from '../components/message-input/message-input.types';
import { type Attachment, AudioAttachment, FileAttachment, GifAttachment, MediaAttachment } from '../models/attachment';

const MAX_FILES = 5;
const MAX_FILE_SIZE = 1024 * 1024 * 200;

export const useMessageInputDropzone = () => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [attachmentMode, setAttachmentMode] = useState<AttachmentMode>(null);

  // Reset mode when attachments cleared
  useEffect(() => {
    if (attachments.length === 0) {
      setAttachmentMode(null);
    }
  }, [attachments.length]);

  // Dynamic accept config based on selected mode
  const accept = useMemo(() => {
    if (attachmentMode === 'media') return mediaAccept;
    if (attachmentMode === 'files') return filesAccept;
    return fullAccept;
  }, [attachmentMode]);

  const dropzoneOptions: DropzoneOptions = {
    accept,
    multiple: true,
    noClick: true,
    maxSize: MAX_FILE_SIZE,
    maxFiles: MAX_FILES,
    validator: (file) => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(i18next.t('messages.upload.fileTooLarge'));
      }
      return null;
    },
    onFileDialogCancel: () => {
      if (attachments.length === 0) {
        setAttachmentMode(null);
      }
    },
    onDrop: async (acceptedFiles, rejectFiles) => {
      if (rejectFiles.length > MAX_FILES || attachments.length + acceptedFiles.length > MAX_FILES) {
        toast.error(i18next.t('messages.upload.maxFilesExceeded', { maxFiles: MAX_FILES }));
        return;
      }

      const newFiles: Attachment[] = await Promise.all(
        acceptedFiles
          .filter(
            (f) =>
              !attachments.some(
                (existingFile) =>
                  existingFile.file?.name === f.name && existingFile.file?.lastModified === f.lastModified
              )
          )
          .map(async (f: File) => {
            switch (f.type) {
              case 'audio/mpeg':
                return new AudioAttachment(f);
              case 'image/gif':
                return new GifAttachment(f);
              case f.type.startsWith('video/') || f.type.startsWith('image/') ? f.type : '':
                return new MediaAttachment(f);
              default:
                return new FileAttachment(f);
            }
          })
      );
      const updateAttachments = [...attachments, ...newFiles];
      if (updateAttachments.length === 0) setAttachmentMode(null);
      setAttachments(updateAttachments);
    }
  };

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, open } = useDropzone(dropzoneOptions);

  /** Open file picker with specific mode (media or files) */
  const openWithMode = (mode: AttachmentMode) => {
    setAttachmentMode(mode);
    // Use setTimeout to ensure state updates before triggering open
    setTimeout(() => open(), 0);
  };

  return {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    open,
    attachments,
    setAttachments,
    attachmentMode,
    setAttachmentMode,
    openWithMode
  };
};
